<script setup>
import { onMounted, onBeforeUnmount, defineEmits, defineModel } from 'vue';

const emit = defineEmits(['scan']);
const isScanning = defineModel('isScanning', { default: false });
const props = defineProps({ active: { type: Boolean, default: true } });

let buffer = '';
let timestamps = [];
let scanInProgress = false;
let lastInput = null;
let timeoutId = null;

const MIN_LENGTH = 3; // minimum barcode length
const MAX_KEY_INTERVAL = 50; // ms between keystrokes for scan (increased for reliability)
const MAX_TOTAL_SCAN_TIME = 300; // total scan duration (increased to accommodate longer barcodes)
const HUMAN_TYPING_GAP = 100; // reset if keypress gap is long (user typing)
const MIN_CHARS_FOR_SCAN = 3; // need at least 3 rapid chars to detect scan

// Helpers
const isChar = (key) => key.length === 1;
const isTextInput = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

function resetScan() {
  buffer = '';
  timestamps = [];
  scanInProgress = false;
  isScanning.value = false; // Update v-model
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
}

function clearInputBuffer() {
  if (lastInput && buffer) {
    const currentValue = lastInput.value || '';
    if (currentValue.endsWith(buffer)) {
      lastInput.value = currentValue.slice(0, -buffer.length);
      lastInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
}

function onKeydown(e) {
  if (!props.active) return;
  const now = Date.now();
  const isCharacter = isChar(e.key);
  const active = document.activeElement;

  if (e.ctrlKey || e.metaKey || e.altKey) return;

  // Track the currently focused input element
  if (isTextInput(active)) {
    lastInput = active;
  }

  // Reset if typing slows down (likely a human)
  if (timestamps.length > 0 && now - timestamps[timestamps.length - 1] > HUMAN_TYPING_GAP) {
    resetScan();
  }

  // If we're in scan mode, prevent characters from entering the input
  if (scanInProgress && isCharacter) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (isCharacter) {
    timestamps.push(now);
    buffer += e.key;

    // Keep sliding window of timestamps
    if (timestamps.length > 30) {
      timestamps.shift();
      buffer = buffer.slice(1);
    }

    // Check if we have enough rapid characters to detect a barcode scan
    // Look at the last MIN_CHARS_FOR_SCAN characters
    const isRapidScan =
      timestamps.length >= MIN_CHARS_FOR_SCAN &&
      timestamps.slice(-MIN_CHARS_FOR_SCAN).every((t, i, arr) =>
        i === 0 || t - arr[i - 1] < MAX_KEY_INTERVAL
      );

    if (isRapidScan && !scanInProgress) {
      scanInProgress = true;
      isScanning.value = true; // Update v-model

      // Clear any leaked characters in input
      setTimeout(() => clearInputBuffer(), 5);

      // Auto-reset scan if it takes too long
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // If we accumulated data, emit it even without Enter
        if (buffer.length >= MIN_LENGTH) {
          emit('scan', buffer);
        }
        resetScan();
      }, MAX_TOTAL_SCAN_TIME);
    }
  }

  // Finalize scan on Enter
  if (e.key === 'Enter' && scanInProgress) {
    e.preventDefault();
    e.stopPropagation();

    if (buffer.length >= MIN_LENGTH) {
      active?.blur(); // close autocomplete
      emit('scan', buffer);
    }

    resetScan();

    // Refocus input after scan completes
    setTimeout(() => {
      if (lastInput) {
        lastInput.focus();
        lastInput = null;
      }
    }, 50);
  }

  // Cancel scan if total time exceeded
  if (timestamps.length >= 2 && now - timestamps[0] > MAX_TOTAL_SCAN_TIME) {
    resetScan();
  }
}

function onBeforeInput(e) {
  if (scanInProgress) {
    e.preventDefault();
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('beforeinput', onBeforeInput, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown, true);
  document.removeEventListener('beforeinput', onBeforeInput, true);
  if (timeoutId) clearTimeout(timeoutId);
});
</script>

<template>
  <div style="display: none;" />
</template>